import { supabase } from '../configs/supabase';
import {
  getUserSubscription,
  getOrCreateFreeSubscription,
  getCurrentUsage,
  recordUsage,
  checkAnalysisLimit as checkSubscriptionLimit,
} from './subscriptionService';

/**
 * Generate a browser fingerprint using various browser characteristics
 * @returns {Promise<string>} Hashed fingerprint string
 */
export const generateBrowserFingerprint = async () => {
  try {
    const components = [];

    // Screen resolution
    components.push(`screen:${window.screen.width}x${window.screen.height}`);
    components.push(`avail:${window.screen.availWidth}x${window.screen.availHeight}`);
    components.push(`color:${window.screen.colorDepth}`);

    // Timezone
    components.push(`tz:${Intl.DateTimeFormat().resolvedOptions().timeZone}`);
    components.push(`offset:${new Date().getTimezoneOffset()}`);

    // Language
    components.push(`lang:${navigator.language}`);
    components.push(`languages:${navigator.languages?.join(',') || ''}`);

    // Platform
    components.push(`platform:${navigator.platform}`);
    components.push(`vendor:${navigator.vendor}`);

    // Canvas fingerprint (simple version)
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Browser fingerprint', 2, 2);
      const canvasData = canvas.toDataURL();
      components.push(`canvas:${canvasData.substring(0, 50)}`);
    } catch (e) {
      // Canvas fingerprinting blocked, skip
      components.push('canvas:blocked');
    }

    // User agent (hashed for privacy)
    const userAgent = navigator.userAgent || '';
    components.push(`ua:${userAgent.length}`);

    // Hardware concurrency
    components.push(`cores:${navigator.hardwareConcurrency || 0}`);

    // Memory (if available)
    if (navigator.deviceMemory) {
      components.push(`memory:${navigator.deviceMemory}`);
    }

    // Combine all components
    const fingerprintString = components.join('|');

    // Simple hash function (not cryptographically secure, but sufficient for fingerprinting)
    let hash = 0;
    for (let i = 0; i < fingerprintString.length; i++) {
      const char = fingerprintString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }

    return Math.abs(hash).toString(36);
  } catch (error) {
    console.error('Error generating browser fingerprint:', error);
    // Fallback to a simple hash based on available data
    return `fallback_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }
};

/**
 * Get user's IP address using a free IP service
 * @returns {Promise<string>} IP address
 */
export const getUserIP = async () => {
  try {
    // Try primary service
    const response = await fetch('https://api.ipify.org?format=json', {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });

    if (response.ok) {
      const data = await response.json();
      return data.ip;
    }
  } catch (error) {
    console.warn('Primary IP service failed, trying fallback:', error);
  }

  // Fallback service
  try {
    const response = await fetch('https://ipapi.co/json/', {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });

    if (response.ok) {
      const data = await response.json();
      return data.ip;
    }
  } catch (error) {
    console.error('All IP services failed:', error);
  }

  // If all services fail, return a placeholder
  // This should rarely happen, but we'll allow analysis to proceed
  return 'unknown';
};

/**
 * Check if user can perform analysis based on their limit
 * @param {string} ipAddress - User's IP address
 * @param {string} fingerprint - Browser fingerprint
 * @returns {Promise<{canAnalyze: boolean, reason?: string, timeUntilReset?: number}>}
 */
export const checkAnalysisLimit = async (ipAddress, fingerprint) => {
  try {
    // Look up existing record
    const { data: existingRecord, error: lookupError } = await supabase
      .from('anonymous_analysis_limits')
      .select('*')
      .eq('ip_address', ipAddress)
      .eq('browser_fingerprint', fingerprint)
      .single();

    if (lookupError && lookupError.code !== 'PGRST116') {
      // Error other than "not found"
      console.error('Error checking analysis limit:', lookupError);
      // Fail open - allow analysis if we can't check
      return { canAnalyze: true, reason: 'check_failed' };
    }

    if (!existingRecord) {
      // No record found - user can analyze
      return { canAnalyze: true };
    }

    // Check if 24 hours have passed since first analysis
    const firstAnalysisTime = new Date(existingRecord.first_analysis_at);
    const now = new Date();
    const hoursSinceFirst = (now - firstAnalysisTime) / (1000 * 60 * 60);

    if (hoursSinceFirst >= 24) {
      // 24 hours have passed - reset the limit
      // Delete old record so a new one can be created
      await supabase
        .from('anonymous_analysis_limits')
        .delete()
        .eq('id', existingRecord.id);

      return { canAnalyze: true, reason: 'limit_reset' };
    }

    // Check if user has already used their daily analysis
    if (existingRecord.analysis_count >= 1) {
      const timeUntilReset = 24 - hoursSinceFirst;
      return {
        canAnalyze: false,
        reason: 'limit_reached',
        timeUntilReset: Math.ceil(timeUntilReset * 60), // minutes until reset
      };
    }

    // User can analyze
    return { canAnalyze: true };
  } catch (error) {
    console.error('Error in checkAnalysisLimit:', error);
    // Fail open - allow analysis if check fails
    return { canAnalyze: true, reason: 'check_failed' };
  }
};

/**
 * Record that an analysis was performed
 * @param {string} ipAddress - User's IP address
 * @param {string} fingerprint - Browser fingerprint
 * @returns {Promise<{success: boolean, error?: any}>}
 */
export const recordAnalysis = async (ipAddress, fingerprint) => {
  try {
    // Check if record exists
    const { data: existingRecord } = await supabase
      .from('anonymous_analysis_limits')
      .select('*')
      .eq('ip_address', ipAddress)
      .eq('browser_fingerprint', fingerprint)
      .single();

    if (existingRecord) {
      // Update existing record
      const { error: updateError } = await supabase
        .from('anonymous_analysis_limits')
        .update({
          last_analysis_at: new Date().toISOString(),
          analysis_count: existingRecord.analysis_count + 1,
        })
        .eq('id', existingRecord.id);

      if (updateError) {
        console.error('Error updating analysis record:', updateError);
        return { success: false, error: updateError };
      }

      return { success: true };
    } else {
      // Create new record
      const { error: insertError } = await supabase
        .from('anonymous_analysis_limits')
        .insert({
          ip_address: ipAddress,
          browser_fingerprint: fingerprint,
          first_analysis_at: new Date().toISOString(),
          last_analysis_at: new Date().toISOString(),
          analysis_count: 1,
        });

      if (insertError) {
        console.error('Error creating analysis record:', insertError);
        return { success: false, error: insertError };
      }

      return { success: true };
    }
  } catch (error) {
    console.error('Error in recordAnalysis:', error);
    return { success: false, error };
  }
};

/**
 * Main function to check if user can perform analysis
 * Checks subscription and usage limits for both signed-in and anonymous users
 * @param {boolean} isSignedIn - Whether user is signed in
 * @param {string} userId - User ID (required if signed in)
 * @returns {Promise<{canAnalyze: boolean, reason?: string, timeUntilReset?: number, subscription?: object, usage?: object, remaining?: number, limit?: number}>}
 */
export const canPerformAnalysis = async (isSignedIn = false, userId = null) => {
  try {
    if (isSignedIn && userId) {
      // Signed-in user: Check subscription and usage
      // Get or create free subscription
      const { subscription, error: subError } = await getOrCreateFreeSubscription(userId);

      if (subError) {
        console.error('Error getting subscription:', subError);
        // Fail open - allow analysis if subscription check fails
        return { canAnalyze: true, reason: 'check_failed' };
      }

      // Get current usage
      const { usage, error: usageError } = await getCurrentUsage(userId, null);

      if (usageError && usageError.code !== 'PGRST116') {
        console.error('Error getting usage:', usageError);
        // Fail open - allow analysis if usage check fails
        return { canAnalyze: true, reason: 'check_failed' };
      }

      // Check limits based on subscription
      const limitCheck = await checkSubscriptionLimit(subscription, usage);

      return {
        ...limitCheck,
        subscription,
        usage,
        userId,
      };
    } else {
      // Anonymous user: Use free tier (1 analysis per 24 hours)
      // Get IP and fingerprint in parallel
      const [ipAddress, fingerprint] = await Promise.all([
        getUserIP(),
        generateBrowserFingerprint(),
      ]);

      // Create anonymous ID from IP and fingerprint
      const anonymousId = `${ipAddress}_${fingerprint}`;

      // Get current usage for anonymous user
      const { usage, error: usageError } = await getCurrentUsage(null, anonymousId);

      if (usageError && usageError.code !== 'PGRST116') {
        console.error('Error getting anonymous usage:', usageError);
        // Fail open - allow analysis if usage check fails
        return { canAnalyze: true, reason: 'check_failed' };
      }

      // Check free tier limit (1 analysis per 24 hours)
      const freeSubscription = { plan_type: 'free', status: 'active' };
      const limitCheck = await checkSubscriptionLimit(freeSubscription, usage);

      return {
        ...limitCheck,
        ipAddress,
        fingerprint,
        anonymousId,
        subscription: freeSubscription,
        usage,
      };
    }
  } catch (error) {
    console.error('Error in canPerformAnalysis:', error);
    // Fail open - allow analysis if check fails
    return { canAnalyze: true, reason: 'check_failed' };
  }
};

/**
 * Record that an analysis was completed
 * Should be called after successful analysis
 * @param {boolean} isSignedIn - Whether user is signed in
 * @param {string} userId - User ID (required if signed in)
 * @param {string} ipAddress - User's IP address (optional, for anonymous users)
 * @param {string} fingerprint - Browser fingerprint (optional, for anonymous users)
 * @param {string} anonymousId - Anonymous ID (optional, will be created if not provided)
 * @returns {Promise<{success: boolean, error?: any}>}
 */
export const recordAnalysisComplete = async (
  isSignedIn = false,
  userId = null,
  ipAddress = null,
  fingerprint = null,
  anonymousId = null
) => {
  try {
    if (isSignedIn && userId) {
      // Record usage for signed-in user
      const result = await recordUsage(userId, null);
      return result;
    } else {
      // Record usage for anonymous user
      let finalAnonymousId = anonymousId;

      if (!finalAnonymousId) {
        // Get IP and fingerprint if not provided
        const [finalIP, finalFingerprint] = await Promise.all([
          ipAddress || getUserIP(),
          fingerprint || generateBrowserFingerprint(),
        ]);

        finalAnonymousId = `${finalIP}_${finalFingerprint}`;
      }

      const result = await recordUsage(null, finalAnonymousId);
      return result;
    }
  } catch (error) {
    console.error('Error recording analysis completion:', error);
    // Don't fail the analysis if recording fails
    return { success: false, error };
  }
};
